import React from "react";
import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Tooltip,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocation, useNavigate } from "react-router-dom";
import { WRITING_WORKSPACE_ENDPOINT } from "../../routes/endpointConstants";

export type SidebarItemType = {
  name?: string;
  subtitle?: string;
  url?: string;
  iconPath?: string;
  iconComponent?: React.ReactNode;
  performFunction?: Function;
  sameTabForOutsideUrl?: boolean; // only works for urls with full http url
  isCollapsed?: boolean;
  locked?: boolean;
  lockedAdornment?: React.ReactNode;
};

export const SidebarItem: React.FC<SidebarItemType> = ({
  isCollapsed = false,
  ...props
}: SidebarItemType) => {
  const navigate = useNavigate();
  const location = useLocation();
  const listItemTextStyle = {
    fontFamily: "Poppins",
    fontSize: "16px",
    fontWeight: 700,
  };

  const handleClick = (e) => {
    if (e.ctrlKey || e.metaKey) {
      const url = props?.url?.toLocaleLowerCase().startsWith("http")
        ? props.url
        : `${window.location.origin}${props.url || "/"}`;
      window.open(url, "_blank");
      return;
    }

    if (props.performFunction && typeof props.performFunction === "function") {
      props.performFunction(e);
      if (props.locked) {
        return;
      }
    }

    if (props.locked) {
      return;
    }

    if (props?.url?.toLocaleLowerCase().startsWith("http")) {
      window.open(
        props.url,
        props.sameTabForOutsideUrl === true ? "_self" : "_blank",
      );
      return;
    }

    if (props.url === WRITING_WORKSPACE_ENDPOINT && e.ctrlKey) {
      window.open(`${window.location.origin}${props.url}`);
    } else {
      navigate(props.url ? props.url : "/");
    }
  };

  const handleMouseDown = (e) => {
    if (e.button === 1) {
      // Middle click
      e.preventDefault();
      const url = props?.url?.toLocaleLowerCase().startsWith("http")
        ? props.url
        : `${window.location.origin}${props.url || "/"}`;
      window.open(url, "_blank");
    }
  };

  const button = (
    <ListItemButton
      className="sidebarItem font-raleway"
      dense
      alignItems="center"
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      selected={!props.locked && location.pathname === props.url}
      sx={{
        minHeight: 48,
        px: 2,
        justifyContent: isCollapsed ? "center" : "flex-start",
        opacity: props.locked ? 0.5 : 1,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? "unset" : 40,
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: isCollapsed ? 0 : 2,
          "& img": {
            width: 20,
            height: 20,
            objectFit: "contain",
          },
          "& svg": {
            width: 20,
            height: 20,
          },
        }}
      >
        {props.iconPath ? (
          <img src={props.iconPath} alt={props.name} />
        ) : (
          props.iconComponent
        )}
      </ListItemIcon>
      {!isCollapsed && (
        <>
          <ListItemText
            disableTypography
            primary={props.name}
            style={listItemTextStyle}
            secondary={
              !props.subtitle ? null : (
                <Typography
                  style={{
                    fontFamily: "Poppins",
                    color: "#5a5a5a",
                    fontSize: "12px",
                  }}
                  variant="subtitle2"
                  noWrap
                  component="div"
                >
                  {props.subtitle}
                </Typography>
              )
            }
          />
          {props.lockedAdornment && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ml: 1,
              }}
            >
              {props.lockedAdornment}
            </Box>
          )}
          {props.url?.toLowerCase().startsWith("http") &&
            props.name !== "Logout" && (
              <OpenInNewIcon
                sx={{ width: 16, height: 16, opacity: 0.6, ml: 1 }}
              />
            )}
        </>
      )}
    </ListItemButton>
  );

  return isCollapsed ? (
    <Tooltip title={props.name} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
};
